/**
 * useWebhookModals - Estado e handlers de modais para Webhooks
 */
import { useState, useCallback } from 'react';
import { Webhook } from '../../../../types';
import { useWebhooks } from './useWebhooks';
import { notify } from '../../../../utils/toast';

export const useWebhookModals = () => {
    const webhooksHook = useWebhooks();

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [webhookToEdit, setWebhookToEdit] = useState<Webhook | undefined>();
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [webhookToDelete, setWebhookToDelete] = useState<Webhook | undefined>();
    const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);

    const openCreate = useCallback(() => {
        setWebhookToEdit(undefined);
        setIsFormOpen(true);
    }, []);

    const openEdit = useCallback((webhook: Webhook) => {
        setWebhookToEdit(webhook);
        setIsFormOpen(true);
    }, []);

    const openDelete = useCallback((webhook: Webhook) => {
        setWebhookToDelete(webhook);
        setIsDeleteOpen(true);
    }, []);

    const save = useCallback(async (data: Omit<Webhook, 'id' | 'createdAt'>) => {
        try {
            if (webhookToEdit) {
                await webhooksHook.updateWebhook(webhookToEdit.id, data);
                notify.success('Webhook atualizado com sucesso!');
            } else {
                await webhooksHook.createWebhook(data);
                notify.success('Webhook criado com sucesso!');
            }
            setIsFormOpen(false);
        } catch {
            notify.error('Erro ao salvar webhook');
        }
    }, [webhookToEdit, webhooksHook]);

    const confirmDelete = useCallback(async () => {
        if (!webhookToDelete) return;
        try {
            await webhooksHook.deleteWebhook(webhookToDelete.id);
            notify.success('Webhook excluído com sucesso!');
            setIsDeleteOpen(false);
            setWebhookToDelete(undefined);
        } catch {
            notify.error('Erro ao excluir webhook');
        }
    }, [webhookToDelete, webhooksHook]);

    const bulkDelete = useCallback(async () => {
        const ids = Array.from(webhooksHook.selectedIds);
        try {
            await webhooksHook.deleteBulk(ids);
            notify.success(`${ids.length} webhook(s) excluído(s)!`);
            setIsBulkDeleteOpen(false);
        } catch {
            notify.error('Erro ao excluir webhooks');
        }
    }, [webhooksHook]);

    const duplicate = useCallback(async (webhook: Webhook) => {
        try {
            const { id, createdAt, ...data } = webhook;
            await webhooksHook.createWebhook({ ...data, name: `${data.name} (Cópia)` });
            notify.success('Webhook duplicado!');
        } catch {
            notify.error('Erro ao duplicar webhook');
        }
    }, [webhooksHook]);

    return {
        // Data
        webhooks: webhooksHook.webhooks,
        loading: webhooksHook.loading,
        selectedIds: webhooksHook.selectedIds,
        toggleSelection: webhooksHook.toggleSelection,
        toggleAllSelection: webhooksHook.toggleAllSelection,
        clearSelection: webhooksHook.clearSelection,
        refetch: webhooksHook.refetch,

        // Modal state
        isFormOpen,
        setIsFormOpen,
        webhookToEdit,
        isDeleteOpen,
        setIsDeleteOpen,
        webhookToDelete,
        isBulkDeleteOpen,
        setIsBulkDeleteOpen,

        // Handlers
        openCreate,
        openEdit,
        openDelete,
        save,
        confirmDelete,
        bulkDelete,
        duplicate,
    };
};
